import { useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Wifi, AlertCircle, Settings, Ban, Loader2 } from "lucide-react";
import { LOGO_SRC } from "../components/RevoraCard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATES = {
  "not-found": {
    icon: AlertCircle,
    title: "This Revora card isn't active",
    body: "We couldn't find this card in our system. If you believe this is a mistake, please contact the business that gave you this card.",
  },
  setup: {
    icon: Settings,
    title: "This card is being set up",
    body: "This Revora card has been registered but its destination hasn't been configured yet. Please check back soon.",
  },
  suspended: {
    icon: Ban,
    title: "This card is currently inactive",
    body: "This Revora card has been temporarily deactivated. Please contact the business for assistance.",
  },
  inactive: {
    icon: Ban,
    title: "This card is no longer in service",
    body: "This Revora card has been retired or replaced. Please contact the business for assistance.",
  },
};

export default function Redirect() {
  const { assetId } = useParams();
  const [params] = useSearchParams();
  const error = params.get("e");

  useEffect(() => {
    if (!error && assetId) {
      const source = params.get("source");
      const qs = source ? `?source=${encodeURIComponent(source)}` : "";
      // hand the browser to the backend routing layer, which records the
      // interaction and 307-redirects to the asset's current destination
      const t = setTimeout(() => {
        window.location.replace(`${BACKEND_URL}/api/r/${encodeURIComponent(assetId)}${qs}`);
      }, 250);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, error]);

  const state = error ? STATES[error] || STATES["not-found"] : null;

  return (
    <div className="min-h-screen bg-ink text-white flex items-center justify-center px-6 noise relative" data-testid="redirect-page">
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-volt/15 blur-3xl rounded-full pointer-events-none" />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="relative text-center max-w-md">
        <img src={LOGO_SRC} alt="Revora Reviews" className="h-10 w-auto mx-auto mb-10" />
        {state ? (
          <div data-testid={`redirect-state-${error}`}>
            <div className="w-14 h-14 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-6">
              <state.icon className="w-6 h-6 text-white/70" />
            </div>
            <h1 className="font-display text-3xl font-black tracking-tight mb-4">{state.title}</h1>
            <p className="text-white/55 text-sm leading-relaxed mb-8">{state.body}</p>
            <Link to="/" className="inline-flex items-center gap-2 border border-white/20 text-white/70 font-bold px-6 py-3 rounded-full hover:border-white hover:text-white transition-colors duration-300 text-sm">
              About Revora Reviews
            </Link>
          </div>
        ) : (
          <div data-testid="redirect-routing">
            <div className="w-14 h-14 rounded-full bg-volt flex items-center justify-center mx-auto mb-6">
              <Wifi className="w-6 h-6 text-white rotate-90" />
            </div>
            <h1 className="font-display text-3xl font-black tracking-tight mb-3">Opening review page…</h1>
            <p className="text-white/50 text-sm flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Routing card {assetId}
            </p>
          </div>
        )}
      </motion.div>
    </div>
  );
}
